/** The complete set of editor commands registered against the
 *  command registry by EditorPage. Each command is a pure spec —
 *  the `run` body closes over `get()` which returns the latest
 *  handler object every time, so commands survive across renders
 *  without having to re-register on every state change.
 *
 *  See `command-registry.ts` for the substrate (matchShortcut,
 *  dispatch, fuzzy-search), and `EditorPage.tsx` for the wiring
 *  (handlersRef + register-once useEffect). When adding a new
 *  command:
 *
 *    1. Add the corresponding handler to EditorCommandHandlers.
 *    2. Wire it from EditorPage (typically a useCallback that calls
 *       existing in-component logic).
 *    3. Add a spec here in the right category.
 *    4. Tests in editor-commands.test.ts.
 *
 *  Hidden commands (`hidden: true`) are the contextual modal-mode
 *  shortcuts — Enter applies the crop / transform / pen-path /
 *  path-edit, Escape cancels them. They aren't useful to surface in
 *  the palette ("Apply Crop" is something you press Enter for, not
 *  something you search for) but they need to live in the registry
 *  so the central keydown listener can dispatch them. */

import type { CommandSpec, CommandShortcut, CommandContext } from './command-registry';
import type { AICapability, RetouchVariant } from '../types';
import type { AdjustmentKind } from '../components/adjustments/registry';

export interface EditorCommandHandlers {
  // ─── File ─────────────────────────────────────────────────────
  newDocument: () => void;
  /** Open the Tier 3 Group D template picker dialog. */
  openNewFromTemplate: () => void;
  openLocal: () => void;
  openFias: () => void;
  /** Save to original location (or local download if no handle). */
  save: () => void;
  /** Open the Save-As / Export dialog. */
  openSaveAs: () => void;
  /** Open the Tier 3 Group D save-as-template prompt (captures the
   *  current canvas size + DPI as a user template). */
  openSaveAsTemplate: () => void;
  closeFile: () => void;
  /** Gates File commands that need an open document. Read at
   *  dispatch / availability time, not at registration time. */
  hasActiveFile: boolean;

  // ─── Image ────────────────────────────────────────────────────
  /** Open the Tier 3 Group D Slice 1d Document Settings dialog
   *  (DPI / units / color mode). */
  openDocumentSettings: () => void;
  /** Open the Canvas Size dialog — resizes the canvas without
   *  resampling pixels (anchor-based shift, visual clipping). */
  openCanvasSize: () => void;

  // ─── Edit ─────────────────────────────────────────────────────
  undo: () => void;
  redo: () => void;
  cut: () => void;
  copy: () => void;
  paste: () => void;
  /** Bound to Delete / Backspace. */
  deleteSelected: () => void;

  // ─── Select ───────────────────────────────────────────────────
  selectAll: () => void;
  invertSelection: () => void;

  // ─── View ─────────────────────────────────────────────────────
  zoomIn: () => void;
  zoomOut: () => void;
  fitToScreen: () => void;
  actualSize: () => void;
  toggleRulers: () => void;
  toggleMeasurementGrid: () => void;
  toggleGuidesVisible: () => void;
  toggleTransparencyChecker: () => void;
  toggleSnapToGrid: () => void;
  toggleSnapToGuides: () => void;
  toggleSmartGuides: () => void;
  toggleLockGuides: () => void;
  clearGuides: () => void;
  openNewGuide: () => void;
  openGridSettings: () => void;

  // ─── Tools (palette selection) ────────────────────────────────
  selectTool: (tool: string) => void;
  /** Retouch sub-tool cycling (Shift+R while Retouch is active). */
  cycleRetouchVariant: () => void;
  /** Activate Retouch tool with a specific variant pre-selected.
   *  Palette-only — surfaced as "Retouch: Clone", "Retouch: Heal",
   *  etc. so each variant is directly searchable instead of forcing
   *  the user through Shift+R cycle. */
  selectRetouchVariant: (variant: RetouchVariant) => void;
  /** Activate the AI tool with a specific capability pre-selected,
   *  skipping the AIToolPickerDialog. Surfaced in the palette as
   *  "AI: Smart Create", "AI: Remove Background", etc. */
  selectAICapability: (capability: AICapability) => void;
  /** Step the active size-based tool's tip up (+1) or down (-1) by a
   *  proportional increment. Bound to ] / [ (Photoshop's bracket
   *  keys). Applies to Draw / Eraser (BrushSettings.width) and
   *  Retouch (RetouchSettings.size); no-op for tools with no tip. */
  adjustToolSize: (direction: 1 | -1) => void;

  // ─── Nudge (Move tool) ────────────────────────────────────────
  /** Translate the active object / selection by (dx, dy) scene
   *  pixels and snapshot history. Bound to the arrow keys (1px) and
   *  Shift+arrows (10px) — Photoshop's Move-tool nudge. No-op when
   *  nothing is selected on the canvas. */
  nudgeSelection: (dx: number, dy: number) => void;

  // ─── Image transforms ─────────────────────────────────────────
  rotateImageLeft: () => void;
  rotateImageRight: () => void;
  flipImageHorizontal: () => void;
  flipImageVertical: () => void;

  // ─── Selection (refinement / AI-powered) ──────────────────────
  /** Clear the active marquee. Photoshop's Ctrl+D. */
  deselectMarquee: () => void;
  /** Open the Color Range dialog (selection from sampled colors). */
  openColorRange: () => void;
  /** Open the Refine Edge workspace for the current selection. */
  openRefineEdge: () => void;

  // ─── Layer ops ────────────────────────────────────────────────
  newLayer: () => void;
  /** Delete the active layer. */
  deleteActiveLayer: () => void;
  /** Toggle visibility of the active layer. */
  toggleActiveLayerVisibility: () => void;
  groupActiveLayer: () => void;
  ungroupActiveLayer: () => void;
  /** Crop the active layer to its alpha bounding box. Refusal
   *  cases (mask present, layer transformed, vector/text/shape
   *  layer, etc.) surface as toasts inside the handler. */
  trimActiveLayer: () => void;
  /** Merge the active layer + every additionally-selected layer
   *  into a single raster layer. Confirm-before-rasterize is the
   *  handler's responsibility — the palette spec only fires when
   *  `hasMultiSelection` is true. */
  mergeActiveSelection: () => void;
  toggleLayersPanel: () => void;

  // ─── Adjustments & filters ────────────────────────────────────
  /** Open an adjustment / filter dialog by kind. Used by every
   *  `adjust.*` and `filter.*` palette spec — the spec passes the
   *  kind so the handler stays a single switch. */
  openAdjustment: (kind: AdjustmentKind) => void;

  // ─── Zoom presets (palette-only) ──────────────────────────────
  /** Zoom to a specific factor (1 = 100%). */
  setZoom: (factor: number) => void;

  // ─── Transform / modal-mode ───────────────────────────────────
  beginTransform: () => void;
  applyTransform: () => void;
  cancelTransform: () => void;
  beginLiquify: () => void;
  applyCrop: () => void;
  finalizePenPath: () => void;
  cancelPenPath: () => void;
  applyPathEdit: () => void;
  cancelPathEdit: () => void;

  // ─── History ──────────────────────────────────────────────────
  /** Photoshop's "New Snapshot" — capture a named, pinned anchor
   *  point in the history. Cap-exempt so a user can return to it
   *  even after 200 subsequent steps push out the regular timeline. */
  historyManualSnapshot: () => void;
  /** Show or hide the History panel (right-edge floating dock). */
  toggleHistoryPanel: () => void;

  // ─── Help / meta ──────────────────────────────────────────────
  openHelp: () => void;
  openPalette: () => void;
  openKeyboardSettings: () => void;
}

/** The tool ids that get a single-letter shortcut, in the legacy
 *  order. The id namespace (`tool.<name>`) is used both as the
 *  registry command id and (with the `tool.` prefix stripped) as
 *  the argument to `selectTool`. Exported so the test suite can
 *  assert every tool has a shortcut. */
export const TOOL_SHORTCUTS: ReadonlyArray<{ tool: string; label: string; key: string }> = [
  { tool: 'move', label: 'Move tool', key: 'v' },
  { tool: 'marquee', label: 'Marquee tool', key: 'm' },
  { tool: 'crop', label: 'Crop tool', key: 'c' },
  { tool: 'draw', label: 'Draw tool', key: 'b' },
  { tool: 'text', label: 'Text tool', key: 't' },
  { tool: 'shape', label: 'Shape tool', key: 'u' },
  { tool: 'eraser', label: 'Eraser tool', key: 'e' },
  { tool: 'fill', label: 'Fill tool', key: 'g' },
  { tool: 'retouch', label: 'Retouch tool', key: 'r' },
  { tool: 'slice', label: 'Slice tool', key: 's' },
  { tool: 'ai', label: 'AI tool', key: 'a' },
];

/** History-entry labels for tool-bound actions. Many editor
 *  actions (brush strokes, marquee selects, erase passes) hit
 *  `history.saveSnapshot()` from canvas event handlers without
 *  going through the command registry — those have no command id
 *  to label by, so the history hook falls back to the active
 *  tool's entry here. The values intentionally read as past-tense
 *  actions, not the tool's display name, so the panel reads
 *  "Brush" / "Erase" rather than "Draw tool" / "Eraser tool". */
export const TOOL_HISTORY_LABELS: Readonly<Record<string, string>> = {
  move: 'Move',
  marquee: 'Marquee select',
  crop: 'Crop',
  draw: 'Brush',
  text: 'Text edit',
  shape: 'Shape',
  eraser: 'Erase',
  fill: 'Fill',
  retouch: 'Retouch',
  slice: 'Slice',
  // Pen tool isn't in TOOL_SHORTCUTS (no single-letter binding —
  // selected from the tool palette / pen-edit affordances) but it
  // is a valid `activeTool` value and produces history entries
  // when the user clicks anchors to build a path.
  pen: 'Pen',
  ai: 'AI edit',
};

/** Build the complete spec list. `get` returns the latest
 *  handler-set on every invocation so commands always see fresh
 *  state — see the EditorPage handlersRef pattern. */
export function buildEditorCommands(get: () => EditorCommandHandlers): CommandSpec[] {
  const specs: CommandSpec[] = [];

  // ─── File ─────────────────────────────────────────────────────
  specs.push(
    {
      id: 'file.new',
      label: 'New',
      category: 'file',
      description: 'Create a new blank document',
      run: () => get().newDocument(),
    },
    {
      id: 'file.new-from-template',
      label: 'New from Template...',
      category: 'file',
      description: 'Create a new document from a built-in or saved template',
      run: () => get().openNewFromTemplate(),
    },
    {
      id: 'file.open-local',
      label: 'Open Local...',
      category: 'file',
      description: 'Open an image from your device',
      run: () => get().openLocal(),
    },
    {
      id: 'file.open-fias',
      label: 'Open from Fias...',
      category: 'file',
      description: 'Open an image saved to Fias storage',
      run: () => get().openFias(),
    },
    {
      id: 'file.save',
      label: 'Save',
      category: 'file',
      description: 'Save to the current location',
      isAvailable: () => get().hasActiveFile,
      run: () => get().save(),
    },
    {
      id: 'file.save-as',
      label: 'Save As...',
      category: 'file',
      description: 'Open the Export dialog',
      shortcuts: [{ key: 's', mod: true }],
      isAvailable: () => get().hasActiveFile,
      run: () => get().openSaveAs(),
    },
    {
      id: 'file.save-as-template',
      label: 'Save as Template...',
      category: 'file',
      description: "Save the active document's canvas size as a reusable template",
      isAvailable: () => get().hasActiveFile,
      run: () => get().openSaveAsTemplate(),
    },
    {
      id: 'file.close',
      label: 'Close',
      category: 'file',
      description: 'Close the active document',
      isAvailable: () => get().hasActiveFile,
      run: () => get().closeFile(),
    },
  );

  // ─── Image ────────────────────────────────────────────────────
  specs.push({
    id: 'image.document-settings',
    label: 'Document Settings...',
    category: 'image',
    description: 'Edit DPI, document size in physical units, and color mode metadata',
    isAvailable: () => get().hasActiveFile,
    run: () => get().openDocumentSettings(),
  });
  specs.push({
    id: 'image.canvas-size',
    label: 'Canvas Size...',
    category: 'image',
    description:
      'Resize the canvas without resampling pixels; pick a 3×3 anchor for where the existing image stays',
    isAvailable: () => get().hasActiveFile,
    run: () => get().openCanvasSize(),
  });
  // Image-level rotate / flip — operate on the whole canvas, NOT
  // on the active layer (layer-level transforms go through Free
  // Transform). Gated on `hasActiveFile` because they're no-ops on
  // an empty editor.
  specs.push(
    {
      id: 'image.rotate-left',
      label: 'Rotate Image 90° Left',
      category: 'image',
      description: 'Rotate the canvas 90° counter-clockwise',
      isAvailable: () => get().hasActiveFile,
      run: () => get().rotateImageLeft(),
    },
    {
      id: 'image.rotate-right',
      label: 'Rotate Image 90° Right',
      category: 'image',
      description: 'Rotate the canvas 90° clockwise',
      isAvailable: () => get().hasActiveFile,
      run: () => get().rotateImageRight(),
    },
    {
      id: 'image.flip-horizontal',
      label: 'Flip Image Horizontal',
      category: 'image',
      description: 'Mirror the canvas left-to-right',
      isAvailable: () => get().hasActiveFile,
      run: () => get().flipImageHorizontal(),
    },
    {
      id: 'image.flip-vertical',
      label: 'Flip Image Vertical',
      category: 'image',
      description: 'Mirror the canvas top-to-bottom',
      isAvailable: () => get().hasActiveFile,
      run: () => get().flipImageVertical(),
    },
  );

  // ─── Adjustments (Image > Adjustments) ────────────────────────
  // All routed through `openAdjustment(kind)` — the same path the
  // adjustments picker uses. Gated on `hasActiveLayer` because the
  // adjustment target is always the active layer (or the resolved
  // smart-object's source pixels).
  const ADJUSTMENT_COMMANDS: ReadonlyArray<{
    kind: AdjustmentKind;
    label: string;
    description: string;
  }> = [
    { kind: 'curves', label: 'Curves', description: 'Per-channel tonal curve editor' },
    { kind: 'levels', label: 'Levels', description: 'Black/white/gamma points per channel' },
    {
      kind: 'brightness-contrast',
      label: 'Brightness/Contrast',
      description: 'Linear brightness and contrast sliders',
    },
    {
      kind: 'shadows-highlights',
      label: 'Shadows/Highlights',
      description: 'Recover shadow detail and tame highlights',
    },
    {
      kind: 'hue-saturation',
      label: 'Hue/Saturation',
      description: 'Shift hue, saturation, and lightness per channel range',
    },
    {
      kind: 'color-balance',
      label: 'Color Balance',
      description: 'Push CMY/RGB casts across shadows / midtones / highlights',
    },
    {
      kind: 'vibrance',
      label: 'Vibrance',
      description: 'Saturation that protects skin tones and already-saturated colors',
    },
    {
      kind: 'black-white',
      label: 'Black & White',
      description: 'Channel-weighted grayscale conversion',
    },
  ];
  for (const a of ADJUSTMENT_COMMANDS) {
    specs.push({
      id: `adjust.${a.kind}`,
      label: a.label,
      category: 'image',
      description: a.description,
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().openAdjustment(a.kind),
    });
  }

  // ─── Filters ──────────────────────────────────────────────────
  // Same dispatch path as adjustments (every entry routes through
  // `openAdjustment`); split into its own category so the palette
  // visually groups them.
  const FILTER_COMMANDS: ReadonlyArray<{
    kind: AdjustmentKind;
    label: string;
    description: string;
  }> = [
    {
      kind: 'filter-gaussian-blur',
      label: 'Gaussian Blur',
      description: 'Soft, even blur with a single radius control',
    },
    {
      kind: 'filter-motion-blur',
      label: 'Motion Blur',
      description: 'Directional linear blur — angle + distance',
    },
    {
      kind: 'filter-radial-blur',
      label: 'Radial Blur',
      description: 'Spin or zoom blur about a center point',
    },
    { kind: 'filter-box-blur', label: 'Box Blur', description: 'Cheap uniform-kernel blur' },
    {
      kind: 'filter-unsharp-mask',
      label: 'Unsharp Mask',
      description: 'Classic sharpen — amount / radius / threshold',
    },
    {
      kind: 'filter-smart-sharpen',
      label: 'Smart Sharpen',
      description: 'Edge-aware sharpen with halo control',
    },
    { kind: 'filter-add-noise', label: 'Add Noise', description: 'Inject monochrome or RGB noise' },
    {
      kind: 'filter-reduce-noise',
      label: 'Reduce Noise',
      description: 'Denoise while preserving edges',
    },
    {
      kind: 'filter-median',
      label: 'Median',
      description: 'Replace each pixel with the median of its neighborhood',
    },
    { kind: 'filter-emboss', label: 'Emboss', description: 'Stylized relief from luminance edges' },
    {
      kind: 'filter-find-edges',
      label: 'Find Edges',
      description: 'Highlight high-contrast transitions',
    },
    {
      kind: 'filter-posterize',
      label: 'Posterize',
      description: 'Quantize tones to a small number of levels',
    },
    {
      kind: 'filter-threshold',
      label: 'Threshold',
      description: 'Binarize the image at a luminance cutoff',
    },
    {
      kind: 'filter-pixelate',
      label: 'Pixelate',
      description: 'Mosaic blocks at a fixed cell size',
    },
    {
      kind: 'filter-lens-correction',
      label: 'Lens Correction',
      description: 'Barrel / pincushion + vertical / horizontal perspective',
    },
    {
      kind: 'filter-vignette',
      label: 'Vignette',
      description: 'Soft darkening or lightening around the edges',
    },
    {
      kind: 'filter-chromatic-aberration',
      label: 'Chromatic Aberration',
      description: 'Shift R / B channels for a lens-fringe look',
    },
    { kind: 'filter-twirl', label: 'Twirl', description: 'Angular swirl around a center point' },
    { kind: 'filter-pinch', label: 'Pinch', description: 'Pinch or bulge a radial region' },
  ];
  for (const f of FILTER_COMMANDS) {
    specs.push({
      id: `filter.${f.kind.replace(/^filter-/, '')}`,
      label: f.label,
      category: 'filter',
      description: f.description,
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().openAdjustment(f.kind),
    });
  }

  // ─── Layer ops ────────────────────────────────────────────────
  specs.push(
    {
      id: 'layer.new',
      label: 'New Layer',
      category: 'layer',
      description: 'Add an empty paint layer above the active layer',
      // No keyboard shortcut: Photoshop's Ctrl/Cmd+Shift+N is reserved
      // by browsers (new incognito window / reopen closed tab) — the
      // page can't reliably intercept it. New Layer stays reachable via
      // the command palette and the Layers panel "+" button.
      isAvailable: () => get().hasActiveFile,
      run: () => get().newLayer(),
    },
    {
      id: 'layer.delete',
      label: 'Delete Layer',
      category: 'layer',
      description: 'Remove the active layer',
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().deleteActiveLayer(),
    },
    {
      id: 'layer.toggle-visibility',
      label: 'Toggle Layer Visibility',
      category: 'layer',
      description: 'Show or hide the active layer',
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().toggleActiveLayerVisibility(),
    },
    {
      id: 'layer.group',
      label: 'Group Layer',
      category: 'layer',
      description: 'Wrap the active layer (or current multi-selection) in a new group',
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().groupActiveLayer(),
    },
    {
      id: 'layer.ungroup',
      label: 'Ungroup',
      category: 'layer',
      description: 'Dissolve the active group, surfacing its children to the parent',
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().ungroupActiveLayer(),
    },
    {
      id: 'layer.trim',
      label: 'Trim Transparent Pixels',
      category: 'layer',
      description: 'Crop the active layer to the bounding box of its non-transparent content',
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().trimActiveLayer(),
    },
    {
      id: 'layer.merge',
      label: 'Merge Layers',
      category: 'layer',
      description:
        'Flatten the active layer + additionally-selected layers into a single raster layer. Confirms first when text / shape / vector layers need rasterization.',
      // Photoshop's Ctrl+E. Gated on multi-selection so a stray
      // press with a single layer selected doesn't silently no-op.
      shortcuts: [{ key: 'e', mod: true }],
      isAvailable: (ctx) => ctx.hasMultiSelection,
      run: () => get().mergeActiveSelection(),
    },
    {
      id: 'view.toggle-layers-panel',
      label: 'Toggle Layers Panel',
      category: 'view',
      description: 'Show or hide the right-side Layers panel',
      run: () => get().toggleLayersPanel(),
    },
  );

  // ─── Zoom presets (palette-only) ──────────────────────────────
  // No default shortcuts — Ctrl+0 / Ctrl+1 already cover Fit /
  // Actual, and additional digit bindings collide with the layer-
  // opacity number shortcuts users expect from Photoshop.
  const ZOOM_PRESETS: ReadonlyArray<{ factor: number; label: string }> = [
    { factor: 0.5, label: 'Zoom to 50%' },
    { factor: 0.75, label: 'Zoom to 75%' },
    { factor: 2, label: 'Zoom to 200%' },
    { factor: 4, label: 'Zoom to 400%' },
  ];
  for (const z of ZOOM_PRESETS) {
    specs.push({
      id: `view.zoom-${Math.round(z.factor * 100)}`,
      label: z.label,
      category: 'view',
      run: () => get().setZoom(z.factor),
    });
  }

  // ─── Edit ─────────────────────────────────────────────────────
  // Redo before Undo so Ctrl+Shift+Z resolves to Redo before falling
  // through to Undo (Ctrl+Z) by Shift-modifier rejection.
  specs.push(
    {
      id: 'edit.undo',
      label: 'Undo',
      category: 'edit',
      shortcuts: [{ key: 'z', mod: true }],
      run: () => get().undo(),
    },
    {
      id: 'edit.redo',
      label: 'Redo',
      category: 'edit',
      shortcuts: [
        // Ctrl+Y (Win/Linux convention) AND Ctrl+Shift+Z (Mac
        // convention) — both bind to the same command.
        { key: 'y', mod: true },
        { key: 'z', mod: true, shift: true },
      ],
      run: () => get().redo(),
    },
    {
      id: 'edit.cut',
      label: 'Cut',
      category: 'edit',
      shortcuts: [{ key: 'x', mod: true }],
      run: () => get().cut(),
    },
    {
      id: 'edit.copy',
      label: 'Copy',
      category: 'edit',
      shortcuts: [{ key: 'c', mod: true }],
      run: () => get().copy(),
    },
    {
      id: 'edit.paste',
      label: 'Paste',
      category: 'edit',
      shortcuts: [{ key: 'v', mod: true }],
      run: () => get().paste(),
    },
    {
      id: 'edit.delete',
      label: 'Delete',
      category: 'edit',
      // Two shortcuts on one command — both Delete and Backspace
      // delete the selected layer / pixels.
      shortcuts: [{ key: 'Delete' }, { key: 'Backspace' }],
      run: () => get().deleteSelected(),
    },
  );

  // ─── Select ───────────────────────────────────────────────────
  specs.push(
    {
      id: 'select.all',
      label: 'Select All',
      category: 'select',
      shortcuts: [{ key: 'a', mod: true }],
      run: () => get().selectAll(),
    },
    {
      id: 'select.invert',
      label: 'Invert Selection',
      category: 'select',
      shortcuts: [{ key: 'i', mod: true, shift: true }],
      run: () => get().invertSelection(),
    },
    {
      id: 'select.deselect',
      label: 'Deselect',
      category: 'select',
      description: 'Clear the active marquee selection',
      shortcuts: [{ key: 'd', mod: true }],
      isAvailable: (ctx) => ctx.hasSelection,
      run: () => get().deselectMarquee(),
    },
    // `select.subject` was removed alongside the on-device imgly model
    // (retired for licensing reasons). The paid Stability/Recraft
    // engines return a composited cutout rather than the binary mask a
    // marquee needs, so there is no drop-in replacement to re-point it
    // at. Re-add the command when a mask-returning backend exists.
    {
      id: 'select.color-range',
      label: 'Color Range...',
      category: 'select',
      description: 'Build a selection from sampled colors with fuzziness and preview',
      isAvailable: () => get().hasActiveFile,
      run: () => get().openColorRange(),
    },
    {
      id: 'select.refine-edge',
      label: 'Refine Edge...',
      category: 'select',
      description: 'Smooth, feather, and decontaminate the current selection edge',
      isAvailable: (ctx) => ctx.hasSelection,
      run: () => get().openRefineEdge(),
    },
  );

  // ─── View ─────────────────────────────────────────────────────
  // Code-based shortcuts (Digit0, Digit1, Equal, Minus, KeyR,
  // Quote, Semicolon) so AZERTY / QWERTZ users hit the same
  // physical positions.
  specs.push(
    {
      id: 'view.zoom-in',
      label: 'Zoom In',
      category: 'view',
      shortcuts: [
        { key: '=', code: 'Equal', mod: true },
        { key: '+', code: 'Equal', mod: true, shift: true },
      ],
      run: () => get().zoomIn(),
    },
    {
      id: 'view.zoom-out',
      label: 'Zoom Out',
      category: 'view',
      shortcuts: [{ key: '-', code: 'Minus', mod: true }],
      run: () => get().zoomOut(),
    },
    {
      id: 'view.fit-screen',
      label: 'Fit on Screen',
      category: 'view',
      shortcuts: [{ key: '0', code: 'Digit0', mod: true }],
      run: () => get().fitToScreen(),
    },
    {
      id: 'view.actual-size',
      label: 'Actual Pixels (100%)',
      category: 'view',
      shortcuts: [{ key: '1', code: 'Digit1', mod: true }],
      run: () => get().actualSize(),
    },
    {
      id: 'view.toggle-rulers',
      label: 'Toggle Rulers',
      category: 'view',
      shortcuts: [{ key: 'r', code: 'KeyR', mod: true }],
      run: () => get().toggleRulers(),
    },
    {
      id: 'view.toggle-grid',
      label: 'Toggle Measurement Grid',
      category: 'view',
      shortcuts: [{ key: "'", code: 'Quote', mod: true }],
      run: () => get().toggleMeasurementGrid(),
    },
    {
      id: 'view.toggle-guides',
      label: 'Toggle Guides',
      category: 'view',
      shortcuts: [{ key: ';', code: 'Semicolon', mod: true }],
      run: () => get().toggleGuidesVisible(),
    },
    // No-shortcut commands below — palette- / menu-discoverable.
    // Photoshop binds shortcuts only to the rulers/grid/guides
    // visibility toggles above; the rest stay menu-only to avoid
    // colliding with the bare letter shortcuts used by tools.
    {
      id: 'view.toggle-transparency-checker',
      label: 'Toggle Transparency Checker',
      category: 'view',
      description: 'Show or hide the checkerboard pattern behind transparent pixels',
      run: () => get().toggleTransparencyChecker(),
    },
    {
      id: 'view.toggle-snap-grid',
      label: 'Toggle Snap to Grid',
      category: 'view',
      run: () => get().toggleSnapToGrid(),
    },
    {
      id: 'view.toggle-snap-guides',
      label: 'Toggle Snap to Guides',
      category: 'view',
      run: () => get().toggleSnapToGuides(),
    },
    {
      id: 'view.toggle-smart-guides',
      label: 'Toggle Smart Guides',
      category: 'view',
      description: 'Auto-align hints when dragging layers; hold Ctrl to suppress',
      run: () => get().toggleSmartGuides(),
    },
    {
      id: 'view.toggle-lock-guides',
      label: 'Toggle Lock Guides',
      category: 'view',
      run: () => get().toggleLockGuides(),
    },
    {
      id: 'view.clear-guides',
      label: 'Clear All Guides',
      category: 'view',
      run: () => get().clearGuides(),
    },
    {
      id: 'view.new-guide',
      label: 'New Guide...',
      category: 'view',
      description: 'Insert a horizontal or vertical guide at a numeric position',
      run: () => get().openNewGuide(),
    },
    {
      id: 'view.open-grid-settings',
      label: 'Grid Settings...',
      category: 'view',
      description: 'Configure measurement grid spacing and color',
      run: () => get().openGridSettings(),
    },
  );

  // ─── Tools ────────────────────────────────────────────────────
  for (const t of TOOL_SHORTCUTS) {
    specs.push({
      id: `tool.${t.tool}`,
      label: t.label,
      category: 'tools',
      shortcuts: [{ key: t.key }],
      run: () => get().selectTool(t.tool),
    });
  }
  // Retouch sub-tool cycling — Shift+R, only while Retouch is the
  // active tool. Registered AFTER `tool.retouch` so the bare-`r`
  // shortcut still wins for the no-modifier case.
  specs.push({
    id: 'tool.retouch.cycle-variant',
    label: 'Cycle Retouch Variant',
    category: 'tools',
    description: 'Switch between Clone / Heal / Smudge / Blur / Sharpen / Dodge / Burn / Sponge',
    shortcuts: [{ key: 'r', shift: true }],
    isAvailable: (ctx) => ctx.activeTool === 'retouch',
    run: () => get().cycleRetouchVariant(),
  });

  // Direct per-variant commands. Activate Retouch + set the variant
  // in a single step so the palette is a one-shot path; the user
  // doesn't need to first pick the Retouch tool then Shift+R N
  // times. Labels include the parent tool ("Retouch: Clone") so
  // they stay distinguishable when the search query is just
  // "clone" or "heal".
  const RETOUCH_VARIANTS: ReadonlyArray<{
    variant: RetouchVariant;
    label: string;
    description: string;
  }> = [
    { variant: 'clone', label: 'Retouch: Clone', description: 'Stamp pixels from a source point' },
    {
      variant: 'heal',
      label: 'Retouch: Heal',
      description: 'Blend source pixels into the target while preserving local tone',
    },
    {
      variant: 'smudge',
      label: 'Retouch: Smudge',
      description: 'Drag pixels in the stroke direction',
    },
    { variant: 'blur', label: 'Retouch: Blur', description: 'Soften pixels under the brush' },
    { variant: 'sharpen', label: 'Retouch: Sharpen', description: 'Enhance local edge contrast' },
    { variant: 'dodge', label: 'Retouch: Dodge', description: 'Lighten pixels under the brush' },
    { variant: 'burn', label: 'Retouch: Burn', description: 'Darken pixels under the brush' },
    { variant: 'sponge', label: 'Retouch: Sponge', description: 'Increase or decrease saturation' },
  ];
  for (const r of RETOUCH_VARIANTS) {
    specs.push({
      id: `tool.retouch.${r.variant}`,
      label: r.label,
      category: 'tools',
      description: r.description,
      run: () => get().selectRetouchVariant(r.variant),
    });
  }

  // Tip-size scaling — Photoshop's [ (shrink) / ] (grow). Gated to
  // the size-based tools so the bracket keys stay free for other
  // uses (and don't preventDefault) when a non-size tool is active.
  const SIZE_TOOLS = new Set(['draw', 'eraser', 'retouch']);
  const sizeToolActive = (ctx: CommandContext) =>
    ctx.activeTool !== null && SIZE_TOOLS.has(ctx.activeTool);
  specs.push(
    {
      id: 'tool.size.decrease',
      label: 'Decrease Tool Size',
      category: 'tools',
      description: 'Shrink the active brush / eraser / retouch tip',
      shortcuts: [{ key: '[' }],
      isAvailable: sizeToolActive,
      run: () => get().adjustToolSize(-1),
    },
    {
      id: 'tool.size.increase',
      label: 'Increase Tool Size',
      category: 'tools',
      description: 'Grow the active brush / eraser / retouch tip',
      shortcuts: [{ key: ']' }],
      isAvailable: sizeToolActive,
      run: () => get().adjustToolSize(1),
    },
  );

  // Arrow-key nudge of the active object — 1px, or 10px with Shift.
  // Photoshop's Move-tool nudge. Hidden from the palette (a pure
  // keyboard convention that would clutter the list as 8 entries)
  // but still customizable via Keyboard Settings. Gated to the Move
  // tool with an active layer so the arrow keys keep their default
  // behavior (page scroll, palette navigation) everywhere else.
  const NUDGE_DIRS = [
    { key: 'ArrowUp', dx: 0, dy: -1, label: 'Nudge Up' },
    { key: 'ArrowDown', dx: 0, dy: 1, label: 'Nudge Down' },
    { key: 'ArrowLeft', dx: -1, dy: 0, label: 'Nudge Left' },
    { key: 'ArrowRight', dx: 1, dy: 0, label: 'Nudge Right' },
  ] as const;
  const nudgeAvailable = (ctx: CommandContext) => ctx.activeTool === 'move' && ctx.hasActiveLayer;
  for (const d of NUDGE_DIRS) {
    specs.push(
      {
        id: `nudge.${d.key.toLowerCase()}`,
        label: d.label,
        category: 'edit',
        hidden: true,
        shortcuts: [{ key: d.key }],
        isAvailable: nudgeAvailable,
        run: () => get().nudgeSelection(d.dx, d.dy),
      },
      {
        id: `nudge.${d.key.toLowerCase()}.large`,
        label: `${d.label} (10px)`,
        category: 'edit',
        hidden: true,
        shortcuts: [{ key: d.key, shift: true }],
        isAvailable: nudgeAvailable,
        run: () => get().nudgeSelection(d.dx * 10, d.dy * 10),
      },
    );
  }

  // AI sub-operations — one palette entry per capability. The
  // AIToolPickerDialog still exists for users who click the AI
  // tool button; these palette entries skip the picker entirely so
  // keyboard-driven users go straight to the sub-panel they want.
  const AI_CAPABILITIES: ReadonlyArray<{
    capability: AICapability;
    label: string;
    description: string;
  }> = [
    {
      capability: 'smart-create',
      label: 'AI: Smart Create',
      description: 'Generate a new image from a text prompt',
    },
    {
      capability: 'smart-delete',
      label: 'AI: Smart Delete',
      description: 'Remove the marqueed region and fill from surrounding pixels',
    },
    {
      capability: 'inpaint',
      label: 'AI: Inpaint',
      description: 'Replace the masked region with prompted content',
    },
    {
      capability: 'search-replace',
      label: 'AI: Search & Replace',
      description: 'Replace an object described by text with something else',
    },
    {
      capability: 'search-recolor',
      label: 'AI: Search & Recolor',
      description: 'Recolor an object described by text',
    },
    {
      capability: 'outpaint',
      label: 'AI: Outpaint',
      description: 'Extend the canvas with AI-generated content',
    },
    {
      capability: 'image-to-image',
      label: 'AI: Image to Image',
      description: 'Reimagine the whole image with a prompt',
    },
    {
      capability: 'remove-background',
      label: 'AI: Remove Background',
      description: 'Strip the background, leaving the subject',
    },
    {
      capability: 'replace-background',
      label: 'AI: Replace Background',
      description: 'Composite the subject onto a new prompted background',
    },
    { capability: 'upscale', label: 'AI: Upscale', description: 'Increase image resolution' },
    {
      capability: 'vectorize',
      label: 'AI: Vectorize',
      description: 'Convert a raster image into a scalable SVG',
    },
    {
      capability: 'control',
      label: 'AI: Control / Guided',
      description: 'Guide generation from a sketch, structure, or reference',
    },
  ];
  for (const a of AI_CAPABILITIES) {
    specs.push({
      id: `ai.${a.capability}`,
      label: a.label,
      category: 'ai',
      description: a.description,
      isAvailable: () => get().hasActiveFile,
      run: () => get().selectAICapability(a.capability),
    });
  }

  // ─── Transform / modal-mode ───────────────────────────────────
  // Begin-mode entry points are surfaced in the palette; modal-mode
  // Enter / Escape commands are hidden because they're context-only
  // and would clutter the palette list.
  specs.push(
    {
      id: 'transform.begin',
      label: 'Free Transform',
      category: 'transform',
      description: 'Move / scale / rotate / skew the active layer',
      shortcuts: [{ key: 't', mod: true }],
      isAvailable: (ctx) => ctx.hasActiveLayer && !ctx.transformActive,
      run: () => get().beginTransform(),
    },
    {
      id: 'transform.liquify',
      label: 'Liquify',
      category: 'transform',
      description: 'Push / pull / pinch / bulge the active layer',
      shortcuts: [{ key: 'x', mod: true, shift: true }],
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().beginLiquify(),
    },
    // Crop apply (Enter while Crop tool is active). The Crop case
    // is registered BEFORE transform.apply so a user holding Crop
    // and somehow also having transformActive (shouldn't happen)
    // still gets Crop semantics — matches the legacy if-else order.
    {
      id: 'crop.apply',
      label: 'Apply Crop',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.activeTool === 'crop',
      run: () => get().applyCrop(),
    },
    {
      id: 'transform.apply',
      label: 'Apply Transform',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.transformActive,
      run: () => get().applyTransform(),
    },
    {
      id: 'transform.cancel',
      label: 'Cancel Transform',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Escape' }],
      isAvailable: (ctx) => ctx.transformActive,
      run: () => get().cancelTransform(),
    },
    {
      id: 'pen.finalize',
      label: 'Finalize Pen Path',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.activeTool === 'pen' && !ctx.transformActive,
      run: () => get().finalizePenPath(),
    },
    {
      id: 'pen.cancel',
      label: 'Cancel Pen Path',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Escape' }],
      isAvailable: (ctx) => ctx.activeTool === 'pen' && !ctx.transformActive,
      run: () => get().cancelPenPath(),
    },
    {
      id: 'path-edit.apply',
      label: 'Apply Path Edit',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.pathEditActive,
      run: () => get().applyPathEdit(),
    },
    {
      id: 'path-edit.cancel',
      label: 'Cancel Path Edit',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Escape' }],
      isAvailable: (ctx) => ctx.pathEditActive,
      run: () => get().cancelPathEdit(),
    },
  );

  // ─── History (Tier 3 Group C Phase 3) ─────────────────────────
  specs.push(
    {
      id: 'history.manual-snapshot',
      label: 'New Snapshot',
      category: 'edit',
      description: 'Pin the current state as a cap-exempt named history anchor',
      // Ctrl+Shift+H — Photoshop's binding for the same feature.
      shortcuts: [{ key: 'h', mod: true, shift: true }],
      run: () => get().historyManualSnapshot(),
    },
    {
      id: 'view.toggle-history-panel',
      label: 'Toggle History Panel',
      category: 'view',
      description: 'Show or hide the History panel — labeled list of every edit with click-to-jump',
      run: () => get().toggleHistoryPanel(),
    },
  );

  // ─── Help / meta ──────────────────────────────────────────────
  specs.push(
    {
      id: 'help.icon-legend',
      label: 'Show Icon Reference',
      category: 'help',
      description: 'Open the icon-meaning cheatsheet',
      run: () => get().openHelp(),
    },
    {
      id: 'palette.open',
      label: 'Open Command Palette',
      category: 'help',
      description: 'Search and run any editor command',
      shortcuts: [{ key: 'k', mod: true }],
      run: () => get().openPalette(),
    },
    {
      id: 'settings.keyboard',
      label: 'Keyboard Shortcuts...',
      category: 'help',
      description: 'Customize keyboard shortcuts',
      // No default shortcut — `Cmd+,` is convention but conflicts
      // with browser-level settings in some platforms, and Phase 2
      // is palette-driven discovery anyway.
      run: () => get().openKeyboardSettings(),
    },
  );

  return specs;
}

/** Exported for tests so future shortcut additions don't accidentally
 *  collide with existing ones (each command has a unique id; each
 *  shortcut tuple is unique within its `isAvailable` band). */
export function shortcutTupleKey(s: CommandShortcut): string {
  return [
    s.code ?? '',
    s.key.toLowerCase(),
    s.mod ? 'M' : '',
    s.ctrl ? 'C' : '',
    s.shift ? 'S' : '',
    s.alt ? 'A' : '',
  ].join(':');
}
